<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useDealsStore } from 'dashboard/stores/deals';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import BarChart from 'shared/components/charts/BarChart.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const dealsStore = useDealsStore();

const report = computed(() => dealsStore.report);

const period = ref('this_month');
const periodOptions = computed(() => [
  { value: 'this_month', label: t('CRM_FUNIL.REPORT.PERIOD.THIS_MONTH') },
  { value: 'last_30', label: t('CRM_FUNIL.REPORT.PERIOD.LAST_30') },
  { value: 'last_90', label: t('CRM_FUNIL.REPORT.PERIOD.LAST_90') },
  { value: 'this_year', label: t('CRM_FUNIL.REPORT.PERIOD.THIS_YEAR') },
]);

// YYYY-MM-DD in the browser's local timezone — same recipe CrmBoard/CrmCard
// already use for follow-up date comparisons, so "today" never drifts a day
// off around midnight UTC.
const toISO = date => date.toLocaleDateString('en-CA');

const rangeForPeriod = value => {
  const today = new Date();
  const to = toISO(today);

  if (value === 'last_30') {
    const from = new Date(today);
    from.setDate(from.getDate() - 29);
    return { from: toISO(from), to };
  }
  if (value === 'last_90') {
    const from = new Date(today);
    from.setDate(from.getDate() - 89);
    return { from: toISO(from), to };
  }
  if (value === 'this_year') {
    return { from: toISO(new Date(today.getFullYear(), 0, 1)), to };
  }
  // 'this_month' (default)
  return {
    from: toISO(new Date(today.getFullYear(), today.getMonth(), 1)),
    to,
  };
};

const loadReport = () => {
  const { from, to } = rangeForPeriod(period.value);
  dealsStore.fetchReport({ from, to });
};

// Each currency in a totals map is formatted and joined on its own — R$ and
// $ are never summed together (same helper as CrmBoard/CrmColumn).
const formatCurrencyMap = (map = {}) => {
  const parts = Object.entries(map)
    .filter(([, cents]) => cents != null)
    .map(([currency, cents]) =>
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format((cents || 0) / 100)
    );
  return parts.join(' · ');
};

const formatReportMap = map =>
  formatCurrencyMap(map) || formatCurrencyMap({ BRL: 0 });

const openTotal = computed(() => formatReportMap(report.value.open));
const wonTotal = computed(() => formatReportMap(report.value.won));
const lostTotal = computed(() => formatReportMap(report.value.lost));

const conversionRate = computed(() => {
  const total = report.value.wonCount + report.value.lostCount;
  if (total === 0) return '—';
  return `${Math.round((report.value.wonCount / total) * 100)}%`;
});

// "2026-03" -> "mar/26". A fixed en-US abbreviation is used on purpose (not
// the active locale) so the label stays compact and consistent across the
// three languages this screen ships in.
const formatMonthLabel = month => {
  const [year, monthIndex] = month.split('-').map(Number);
  const date = new Date(year, monthIndex - 1, 1);
  const shortMonth = date
    .toLocaleDateString('en-US', { month: 'short' })
    .toLowerCase();
  return `${shortMonth}/${String(year).slice(-2)}`;
};

const wonByMonth = computed(() => report.value.wonByMonth);
const chartCategories = computed(() =>
  wonByMonth.value.map(entry => formatMonthLabel(entry.month))
);

const monthHasCurrency = currency =>
  wonByMonth.value.some(entry => entry.totals?.[currency] != null);

const CURRENCY_COLORS = {
  BRL: 'rgb(var(--blue-9))',
  USD: 'rgb(var(--amber-9))',
};

const chartSeries = computed(() => {
  const currencies = ['BRL', ...(monthHasCurrency('USD') ? ['USD'] : [])];
  return currencies.map(currency => ({
    id: currency,
    label: currency,
    color: CURRENCY_COLORS[currency],
    data: wonByMonth.value.map(entry => (entry.totals?.[currency] || 0) / 100),
  }));
});

const chartData = computed(() => ({
  categories: chartCategories.value,
  series: chartSeries.value,
}));

const byAssignee = computed(() => report.value.byAssignee);

const backToPipeline = () =>
  router.push({
    name: 'crm_index',
    params: { accountId: route.params.accountId },
  });

onMounted(loadReport);
watch(period, loadReport);
</script>

<template>
  <div class="flex flex-col w-full h-full p-4 gap-4 overflow-y-auto">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <Button
          icon="i-lucide-arrow-left"
          variant="ghost"
          color="slate"
          size="sm"
          :label="t('CRM_FUNIL.REPORT.BACK')"
          @click="backToPipeline"
        />
        <h1 class="text-lg font-medium text-n-slate-12">
          {{ t('CRM_FUNIL.REPORT.TITLE') }}
        </h1>
      </div>
      <Select
        v-model="period"
        :options="periodOptions"
        :aria-label="t('CRM_FUNIL.REPORT.TITLE')"
      />
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div
        class="p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2"
      >
        <p class="text-xs text-n-slate-11">
          {{ t('CRM_FUNIL.REPORT.CARD.OPEN') }}
        </p>
        <p class="text-lg font-medium text-n-slate-12">{{ openTotal }}</p>
      </div>
      <div
        class="p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2"
      >
        <p class="text-xs text-n-slate-11">
          {{ t('CRM_FUNIL.REPORT.CARD.WON') }}
        </p>
        <p class="text-lg font-medium text-n-teal-11">{{ wonTotal }}</p>
      </div>
      <div
        class="p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2"
      >
        <p class="text-xs text-n-slate-11">
          {{ t('CRM_FUNIL.REPORT.CARD.LOST') }}
        </p>
        <p class="text-lg font-medium text-n-ruby-9">{{ lostTotal }}</p>
      </div>
      <div
        class="p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2"
      >
        <p class="text-xs text-n-slate-11">
          {{ t('CRM_FUNIL.REPORT.CARD.CONVERSION') }}
        </p>
        <p class="text-lg font-medium text-n-slate-12">
          {{ conversionRate }}
        </p>
      </div>
    </div>

    <div
      class="p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2"
    >
      <p class="text-sm font-medium text-n-slate-12 mb-4">
        {{ t('CRM_FUNIL.REPORT.WON_BY_MONTH') }}
      </p>
      <div class="h-72">
        <BarChart
          :data="chartData"
          :aria-label="t('CRM_FUNIL.REPORT.WON_BY_MONTH')"
          :height="288"
        />
      </div>
    </div>

    <div
      class="p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2"
    >
      <p class="text-sm font-medium text-n-slate-12 mb-4">
        {{ t('CRM_FUNIL.REPORT.BY_ASSIGNEE') }}
      </p>
      <p v-if="!byAssignee.length" class="text-sm text-n-slate-11">
        {{ t('CRM_FUNIL.REPORT.EMPTY') }}
      </p>
      <ul v-else class="flex flex-col gap-2">
        <li
          v-for="(item, index) in byAssignee"
          :key="item.assignee_id ?? `unassigned-${index}`"
          class="flex items-center justify-between text-sm py-1 border-b border-n-weak last:border-0"
        >
          <span class="text-n-slate-12">
            {{ item.name || t('CRM_FUNIL.REPORT.UNASSIGNED') }}
          </span>
          <span class="font-medium text-n-slate-12">
            {{ formatReportMap(item.totals) }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>
